/**
 * feedSticky.js — which posts pin above the stream as "Requires your attention",
 * and which posts count toward the unread greeting. (RW-2263 / RW-2262)
 *
 * Before this, an un-acknowledged important/critical post was sticky forever:
 * eleven April posts (one of them literally "Meow") sat above every agent's feed
 * for five months, and the twelve bot-written FLOW recaps each counted as an
 * unread post no human wrote. Two rules, in one place so Feed.js and
 * FeedLayout.js cannot drift:
 *
 *   1. A priority post pins only inside the acknowledge window. After it the
 *      card returns to the stream as a normal post; the Acknowledge button
 *      stays on the card because PostCard renders it from `priority`, not from
 *      stickiness.
 *   2. Bot posts never pin and never count as unread. A bot post is one the
 *      system wrote: `autoGenerated: true` (every cron since RW-743) or
 *      `authorUid: 'system'` (the retired Friday leaderboard set only that —
 *      six of them sit in prod marked important). Their priority is not
 *      trusted either — a cron must never set it, and the ones that did are
 *      retired by scripts/rw2258-social-cleanup.js.
 *
 * Pure: no Firestore, no React. Timestamps may be Firestore Timestamps, Dates,
 * ISO strings, or absent (a just-written post whose serverTimestamp has not
 * resolved yet — treated as brand new so it pins immediately, as it always did).
 */

export const ACK_WINDOW_DAYS = 7;
export const ACK_WINDOW_MS = ACK_WINDOW_DAYS * 86400000;

const PINNED_PRIORITIES = ['important', 'critical'];

export function isPriorityPost(post) {
  return !!post && PINNED_PRIORITIES.includes(post.priority);
}

/** A post the system wrote, whichever cron wrote it. */
export function isBotPost(post) {
  return !!post && (post.autoGenerated === true || post.authorUid === 'system');
}

/** Epoch ms of the post's timestamp, or null when it is missing or unparseable. */
export function postTimeMs(post) {
  const ts = post?.timestamp;
  if (ts == null) return null;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  const ms = (ts instanceof Date ? ts : new Date(ts)).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** True while the post is younger than the acknowledge window. No timestamp = brand new. */
export function isWithinAckWindow(post, now = Date.now()) {
  const t = postTimeMs(post);
  if (t === null) return true;
  return now - t < ACK_WINDOW_MS;
}

/**
 * Should this post sit in "Requires your attention" for `uid` right now?
 * important/critical · not a bot post · not yet acknowledged by uid · inside the window.
 */
export function isStickyPost(post, uid, now = Date.now()) {
  if (!post || !uid) return false;
  if (!isPriorityPost(post)) return false;
  if (isBotPost(post)) return false;
  if ((post.acknowledgedBy || []).includes(uid)) return false;
  return isWithinAckWindow(post, now);
}

/** Does this post count toward "N unread"? Bot posts don't. */
export function countsAsUnread(post) {
  return !!post && !isBotPost(post);
}
